import type React from 'react';
import { type ReactElement, useId } from 'react';

import classNames from 'classnames';

import type { IconType } from '../../icons/constants';
import Icon from '../../icons/icon';
import type { FCC } from '../../types/common';
import { ui } from '../../utils/helpers';

import mixins from '../../styles/core/mixins.module.css';
import styles from './collapsible.module.css';

type Props = {
  className?: string;
  contentClassName?: string;
  triggerClassName?: string;
  trigger: string | ReactElement;
  onTriggerClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  showContent: boolean;
  uiKey?: string;
  triggerIconClassName?: string;
  dividerClassName?: string;
  icon?: IconType;
  displayTriggerDivider?: boolean;
};

type TriggerDividerProps = {
  dividerClassName?: string;
};

const TriggerDivider = ({ dividerClassName }: TriggerDividerProps) => {
  return <div className={dividerClassName} />;
};

const Collapsible: FCC<Props> = ({
  children,
  className,
  contentClassName,
  onTriggerClick,
  showContent,
  trigger,
  triggerClassName,
  uiKey,
  triggerIconClassName,
  icon,
  dividerClassName,
  displayTriggerDivider,
}) => {
  const id = useId();

  return (
    <div className={className} {...ui(uiKey)}>
      <button
        className={classNames(
          styles.trigger,
          triggerClassName,
          mixins.reset_button,
        )}
        onClick={onTriggerClick}
        type="button"
        aria-expanded={showContent}
        aria-controls={id}
      >
        {displayTriggerDivider && (
          <TriggerDivider dividerClassName={dividerClassName} />
        )}
        {trigger}
        {icon && <Icon icon={icon} className={triggerIconClassName} />}
        {displayTriggerDivider && (
          <TriggerDivider dividerClassName={dividerClassName} />
        )}
      </button>
      <div
        className={classNames(contentClassName, {
          [styles.hidden]: !showContent,
        })}
        id={id}
      >
        {children}
      </div>
    </div>
  );
};

export default Collapsible;
